import * as React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { Translate } from '../../../../shared/LanguageService';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import * as PhotoStatus from '../../../../shared/PhotoStatus';
import { useQuery } from 'react-query';
import { getOrderPhotoStatus } from './queries/getOrderPhotoStatus';
import { getOrderImageReference } from './queries/getOrderImageReference';
import { useOrderImages, useRemoveImage } from './hooks/useRemoveImage';
import SEPlayLink from './components/SEPlayLink';
import SEPlayEditButton from './components/SEPlayEditButton';
import { DialogImage, SEPlayHandlingLabels } from './SEPlayTypes';
import SEPlayEditDialog from './SEPlayEditDialog';
import SEPlayAddDialog from './SEPlayAddDialog';

export type SEPlayHandlingProps = {
    labels: SEPlayHandlingLabels;
    imageGalleryUrl: string;
    completeUrl: string;
    currentStatus: number;
    images: DialogImage[];
    handleUpdateImage: any;
};

const SEPlayNoLinkMessage = ({ errorMessage }) => {
    return <Typography>{errorMessage}</Typography>;
};

export const SEPlayHandling = (props: SEPlayHandlingProps) => {
    let {
        imageGalleryUrl,
        completeUrl,
        labels,
        currentStatus,
        images,
        handleUpdateImage,
    } = props;

    let initialImageList = images;

    const [openEditSlideshow, setOpenEditSlideshow] = React.useState<boolean>(false);
    const [showAddImagesView, setShowAddImagesView] = React.useState<boolean>(false);
    const [imageListItems, setImageListItems] = React.useState<DialogImage[]>(initialImageList);
    const [isHandlingImageListState, setIsHandlingImageListState] = React.useState<boolean>(false);

    React.useEffect(() => {
        initialImageList.forEach((image, index) => {
            setImageListItems((prevState) => {
                const newState = prevState.map((element) => {
                    if (element.reference === image.reference) {
                        return { ...element, orderInSlideshow: index };
                    }
                    return element;
                });
                return newState;
            });
        });
    }, []);

    React.useEffect(() => {
        if (isHandlingImageListState) {
            imageListItems.forEach((image) => {
                setImageListItems((prevState) => {
                    const newState = prevState.map((element, index) => {
                        if (element.reference === image.reference) {
                            return { ...element, orderInSlideshow: index };
                        }
                        return element;
                    });
                    return newState;
                });
            });
            setIsHandlingImageListState(false);
        }
    }, [isHandlingImageListState]);

    const handleEditButton = () => {
        setOpenEditSlideshow(!openEditSlideshow);
    };

    const handleAddImagesView = () => {
        setShowAddImagesView(true);
    };

    const handleAddedImages = (images) => {
        images.forEach((image) => {
            setImageListItems((prevState) => {
                const newState = prevState.map((element) => {
                    if (element.reference === image.reference) {
                        return { ...element, includedInSlideshow: true };
                    }
                    return element;
                });
                return newState;
            });
        });
    };

    const handleRemovedImages = (image) => {
        setImageListItems((prevState) => {
            const newState = prevState.map((element) => {
                if (element.reference === image.reference) {
                    return { ...element, includedInSlideshow: false };
                }
                return element;
            });
            return newState;
        });
    };

    const handleSubmit = () => {
        handleUpdateImage(imageListItems);
    };

    const drop = (dragItem: number, dragOverItem: number) => {
        const copyListItems = [...imageListItems];
        const dragItemContent = copyListItems[dragItem];
        copyListItems.splice(dragItem, 1);
        copyListItems.splice(dragOverItem, 0, dragItemContent);
        setImageListItems(copyListItems);
        setIsHandlingImageListState(true);
    };

    if (currentStatus >= PhotoStatus.CustomerApproved && imageGalleryUrl) {
        return (
            <>
                <Grid container display={'flex'} alignItems={'center'}>
                    <SEPlayLink
                        labels={labels}
                        imageGalleryUrl={imageGalleryUrl}
                        completeUrl={completeUrl}
                    />
                    <SEPlayEditButton
                        label={labels.editButton}
                        handleEditButton={handleEditButton}
                    />
                </Grid>
                <SEPlayEditDialog
                    onSubmit={handleSubmit}
                    openEditSlideshow={openEditSlideshow}
                    onClose={() => setOpenEditSlideshow(false)}
                    imageList={imageListItems}
                    labels={labels}
                    handleAddImagesView={handleAddImagesView}
                    handleRemovedImages={handleRemovedImages}
                    onDrop={drop}
                />
                {showAddImagesView ? (
                    <SEPlayAddDialog
                        openEditSlideshow={openEditSlideshow}
                        onClose={() => setOpenEditSlideshow(false)}
                        images={imageListItems}
                        labels={labels}
                        handleAddImagesView={handleAddImagesView}
                        showAddImagesView={showAddImagesView}
                        onCloseAddImagesView={() => setShowAddImagesView(false)}
                        handleUpdateImage={handleUpdateImage}
                        handleAddedImages={handleAddedImages}
                    />
                ) : null}
            </>
        );
    }
    return <SEPlayNoLinkMessage errorMessage={labels.errorMessage} />;
};

function HandleImagesDataLayer(props) {
    let { orderId } = props;
    let { imageGalleryReference } = props;
    let { labels } = props;

    const { mutate: updateImage, isLoading: isSlideShowUpdating } = useRemoveImage();

    const handleUpdateImage = (imagesArray) => {
        let imageReference = imagesArray[0].reference;
        let image = { orderId, imageReference, imagesArray };
        updateImage(image);
    };

    let { isLoading, data } = useOrderImages(orderId, imageGalleryReference);
    if (isLoading || isSlideShowUpdating) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    let imagesArray = data ? data['images'] : null;

    let images = imagesArray
        ? imagesArray.map((image, index) => {
            if (!image.reference !== undefined)
                return {
                    index: index,
                    reference: image.reference,
                    thumbnail: `/api/file/${image.reference}`,
                    includedInSlideshow: image.includedInSlideshow,
                    orderInSlideshow: image.orderInSlideshow
                };
        })
        : [];

    return (
        <SEPlayHandling
            {...props}
            labels={labels}
            images={images}
            handleUpdateImage={handleUpdateImage}
        />
    );
}

function TranslatedSEPlayHandling(props: SEPlayHandlingProps) {
    let labels: SEPlayHandlingLabels = {
        linkLabel: Translate('image-processing/page/status-actions/description/image-gallery'),
        tooltipLabel: Translate('image-processing/se-play-handling/tooltip/copy-link'),
        errorMessage: Translate('image-processing/se-play-handling/error-message/no-link'),
        dialogTitle: Translate('image-processing/se-play-handling/dialog-title'),
        dialogSaveButton: Translate('image-processing/se-play-handling/dialog-save-button'),
        dialogDoneButton: Translate('image-processing/se-play-handling/dialog-done-button'),
        editButton: Translate('image-processing/se-play-handling/edit-button'),
        addImageTooltip: Translate('image-processing/se-play-handling/add-image-tooltip'),
        removeImageTooltip: Translate('image-processing/se-play-handling/remove-image-tooltip'),
        addButtonLabel: Translate('image-processing/se-play-handling/add-images-button-label'),
        dragTooltip: Translate('image-processing/se-play-handling/drag-tooltip')
    };

    return <HandleImagesDataLayer {...props} labels={labels} />;
}

function SEPlayDataLayer(props: any) {
    let { orderId } = props;

    const orderPhotoStatus = useQuery('status', () => getOrderPhotoStatus(orderId));
    const orderImageReference = useQuery('reference', () => getOrderImageReference(orderId));

    if (orderPhotoStatus.isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    let currentStatus = orderPhotoStatus.data['currentStatus'];

    if (orderImageReference.isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    let imageGalleryReference = orderImageReference.data['imageGalleryReference'];
    let reference = imageGalleryReference ? imageGalleryReference : null;

    let imageGalleryUrl =
        imageGalleryReference != null
            ? '/imagegallery?reference=' + imageGalleryReference.toUpperCase()
            : null;

    let baseUrl = window.location.origin;

    let completeUrl =
        imageGalleryReference != null
            ? `${baseUrl}` + '/imagegallery?reference=' + imageGalleryReference.toUpperCase()
            : null;

    return (
        <TranslatedSEPlayHandling
            {...props}
            completeUrl={completeUrl}
            imageGalleryUrl={imageGalleryUrl}
            imageGalleryReference={reference}
            currentStatus={currentStatus}
        />
    );
}

export default SEPlayDataLayer;
